import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

import type { Image } from '../../services/api';
import api from '../../services/api';

import styles from './ImagePickerModal.module.css';
import ImageUploader from './ImageUploader';

/**
 * ImagePickerModal Props
 */
type ImagePickerModalProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSelect: (image: Image) => void;
  readonly title?: string;
};

/**
 * ImagePickerModal Component
 *
 * Modal for selecting or uploading images.
 * Features:
 * - Two tabs: Select Existing | Upload New
 * - Grid of existing images with search
 * - Image uploader for new uploads
 * - Returns selected image to parent
 */
const ImagePickerModal: React.FC<ImagePickerModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  title = 'Select Image',
}) => {
  const [activeTab, setActiveTab] = useState<'existing' | 'upload'>('existing');
  const [images, setImages] = useState<ReadonlyArray<Image>>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImage, setSelectedImage] = useState<Image | null>(null);
  const [uploading, setUploading] = useState(false);

  /**
   * Fetch existing images
   */
  const fetchImages = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch all images (admin route returns all)
      const response = await api.images.getImages('all', 1, 100);
      setImages(response.images);
    } catch (error) {
      console.error('Failed to fetch images:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch images when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchImages();
    }
  }, [isOpen, fetchImages]);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  /**
   * Filter images by search term
   */
  const filteredImages = images.filter((image) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      image.title?.toLowerCase().includes(term) ||
      image.alt?.toLowerCase().includes(term) ||
      image.filename?.toLowerCase().includes(term)
    );
  });

  /**
   * Handle image selection
   */
  const handleImageClick = (image: Image) => {
    setSelectedImage(image);
  };

  /**
   * Confirm selection
   */
  const handleConfirm = () => {
    if (selectedImage) {
      onSelect(selectedImage);
      onClose();
    }
  };

  /**
   * Handle image upload
   */
  const handleUpload = async (files: ReadonlyArray<File>) => {
    try {
      setUploading(true);
      const uploadedImages = await api.images.uploadImages([...files], 'blog');
      if (uploadedImages && uploadedImages.length > 0) {
        // Select the first uploaded image
        const newImage = uploadedImages[0] as Image;
        onSelect(newImage);
        onClose();
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <h2>{title}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            &times;
          </button>
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'existing' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('existing')}
          >
            Select Existing
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'upload' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('upload')}
          >
            Upload New
          </button>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {activeTab === 'existing' ? (
            <>
              {/* Search */}
              <div className={styles.searchBar}>
                <input
                  type="text"
                  placeholder="Search images..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </div>

              {/* Image Grid */}
              <div className={styles.imageGrid}>
                {loading ? (
                  <div className={styles.loading}>Loading images...</div>
                ) : filteredImages.length === 0 ? (
                  <div className={styles.empty}>No images found</div>
                ) : (
                  filteredImages.map((image) => (
                    <div
                      key={image._id}
                      className={`${styles.imageItem} ${
                        selectedImage?._id === image._id ? styles.selected : ''
                      }`}
                      onClick={() => handleImageClick(image)}
                    >
                      <img
                        src={image.thumbnailUrl || image.url}
                        alt={image.alt ?? image.title ?? 'Image'}
                      />
                      {image.title && <span className={styles.imageTitle}>{image.title}</span>}
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className={styles.uploadTab}>
              <ImageUploader
                onUpload={handleUpload}
                maxFiles={1}
                loading={uploading}
              />
              {uploading && <div className={styles.uploading}>Uploading...</div>}
            </div>
          )}
        </div>

        {/* Footer */}
        {activeTab === 'existing' && (
          <div className={styles.footer}>
            <button className={styles.cancelButton} onClick={onClose}>
              Cancel
            </button>
            <button
              className={styles.selectButton}
              onClick={handleConfirm}
              disabled={!selectedImage}
            >
              Select Image
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ImagePickerModal;
